import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { SystemStyleObject } from '@mui/system';
import { defaultResponsiveContentMaxWidth, responsiveFillColumnSx, responsivePagePaddingX } from '../utils/responsiveLayout';

interface ResponsivePageProps extends Omit<BoxProps, 'children'> {
  children: React.ReactNode;
  contentMaxWidth?: number | string;
  contentSx?: SxProps<Theme>;
}

const resolveSx = (sxValue: SxProps<Theme> | undefined, theme: Theme): SystemStyleObject<Theme> => {
  if (!sxValue) {
    return {};
  }

  if (typeof sxValue === 'function') {
    return sxValue(theme) as SystemStyleObject<Theme>;
  }

  if (Array.isArray(sxValue)) {
    return sxValue.reduce<SystemStyleObject<Theme>>((acc, item) => {
      if (!item) {
        return acc;
      }

      return {
        ...acc,
        ...resolveSx(item as SxProps<Theme>, theme),
      };
    }, {});
  }

  return sxValue as SystemStyleObject<Theme>;
};

const ResponsivePage: React.FC<ResponsivePageProps> = ({
  children,
  sx,
  contentSx,
  contentMaxWidth = defaultResponsiveContentMaxWidth,
  ...rest
}) => {
  return (
    <Box
      {...rest}
      sx={(theme) => ({
        ...responsiveFillColumnSx,
        px: responsivePagePaddingX,
        ...resolveSx(sx, theme),
      })}
    >
      <Box
        sx={(theme) => ({
          ...responsiveFillColumnSx,
          maxWidth: contentMaxWidth,
          mx: 'auto',
          ...resolveSx(contentSx, theme),
        })}
      >
        {children}
      </Box>
    </Box>
  );
};

export default ResponsivePage;
